/**
 * Model_Router fallback chain and per-model free-tier limits.
 *
 * Limits sourced from the user's actual Google AI Studio rate-limit
 * dashboard (free tier).
 *
 * Priority order — highest RPD first so the workhorse model (3.1 Flash
 * Lite, 500 RPD) carries the bulk of the conversation, with quality
 * fallbacks ready to step in silently on rate-limit / 5xx / safety
 * blocks.
 *
 *   1. gemini-3.1-flash-lite  — 15 RPM, 500 RPD  ← MAIN workhorse
 *   2. gemini-2.5-flash-lite  — 10 RPM, 20 RPD   (cheap fallback)
 *   3. gemini-2.5-flash       — 5 RPM,  20 RPD   (quality fallback)
 *   4. gemini-3-flash         — 5 RPM,  20 RPD   (preview, healthy)
 *   5. gemini-3.5-flash       — 5 RPM,  20 RPD   (last resort; this
 *                                                 model has been
 *                                                 observed returning
 *                                                 HTTP 503 frequently,
 *                                                 so it is tried last)
 *
 * All five model IDs were verified against the live
 * `…/v1beta/models` listing and a `generateContent` smoke test on the
 * configured key (May 2026): 3.1-flash-lite, 2.5-flash-lite, 2.5-flash
 * and 3-flash-preview returned 200; 3.5-flash returned 503 — hence its
 * last-resort position. The executor's transient-skip logic advances
 * past a 503ing model automatically, so its presence never traps a turn.
 *
 * Excluded from the chain:
 *   - gemini-2.0-flash / gemini-2.0-flash-lite — 0/0 free tier quota.
 */

import type { ModelId } from './types';

export const FALLBACK_CHAIN = [
  'gemini-3.1-flash-lite',
  'gemini-2.5-flash-lite',
  'gemini-2.5-flash',
  'gemini-3-flash',
  'gemini-3.5-flash',
] as const satisfies readonly ModelId[];

/**
 * Free-tier limits from the user's Google AI Studio dashboard.
 * The executor uses these to preemptively skip a model at its limit.
 */
export const MODEL_LIMITS: Record<ModelId, { rpm: number; rpd: number }> = {
  'gemini-3.1-flash-lite': { rpm: 15, rpd: 500 },
  'gemini-2.5-flash-lite': { rpm: 10, rpd: 20  },
  'gemini-2.5-flash':      { rpm: 5,  rpd: 20  },
  'gemini-3-flash':        { rpm: 5,  rpd: 20  },
  'gemini-3.5-flash':      { rpm: 5,  rpd: 20  },
};

/**
 * Actual Gemini API model name strings used in the request URL.
 *
 * Verified against the live model listing for the configured key
 * (May 2026). `gemini-3-flash` maps to the `-preview` suffixed name
 * because that is the published id; the others are published without a
 * suffix.
 */
export const MODEL_API_NAMES: Record<ModelId, string> = {
  'gemini-3.1-flash-lite': 'gemini-3.1-flash-lite',
  'gemini-2.5-flash-lite': 'gemini-2.5-flash-lite',
  'gemini-2.5-flash':      'gemini-2.5-flash',
  'gemini-3-flash':        'gemini-3-flash-preview',
  'gemini-3.5-flash':      'gemini-3.5-flash',
};
